import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "../lib/api";
import { clearAuthContext, initializeOrganization, setActiveOrgId } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BarChart3, Mail, Lock, ArrowRight } from "lucide-react";
import ProfitSystemLogo from "../components/ProfitSystemLogo";
import { toast } from "sonner";
import { API } from "../App";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInviteFlow = searchParams.get("invite") === "1";
  const inviteToken = searchParams.get("token") || sessionStorage.getItem("invite_token");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/login`, formData, {
          withCredentials: true,
          timeout: 10000,
        });
        const { token, user } = response.data;
        clearAuthContext();
        localStorage.setItem("auth_token", token);
        localStorage.setItem("token", token);
        const orgResult = await Promise.race([
          initializeOrganization(user?.user_id),
          new Promise(r => setTimeout(r, 3000))
        ]);
        toast.success("¡Bienvenido de vuelta!");

        // If invite flow, redirect back to accept invite page
        if (isInviteFlow && inviteToken) {
          sessionStorage.removeItem("invite_token");
          navigate(`/invite/accept?token=${inviteToken}`);
        } else {
          navigate("/dashboard", { state: { user } });
        }
        return;
      } catch (error) {
        const status = error?.response?.status;
        const isServerBusy = !status || status >= 500 || error.code === "ECONNABORTED";
        if (isServerBusy && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        toast.error(error.response?.data?.detail || "Error al iniciar sesión. Inténtalo de nuevo.");
      }
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-8">
              <ProfitSystemLogo height={44} />
            </Link>
            
            <h1 className="text-3xl font-black tracking-tight text-slate-900">
              Bienvenido de vuelta
            </h1>
            <p className="text-slate-600 mt-2">
              Inicia sesión para acceder a tu dashboard
            </p>
          </div>

          {/* Invite flow banner */}
          {isInviteFlow && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800" data-testid="invite-login-banner">
              Inicia sesión para aceptar tu invitación al equipo.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10 h-12 border-slate-200 focus:border-red-500 focus:ring-red-500"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[#C8192C] hover:bg-[#A0141F] text-white font-medium btn-press"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">O continúa con</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-slate-200 hover:bg-slate-50"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continuar con Google
          </Button>

          <p className="text-center text-slate-600">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-red-600 hover:text-red-700 font-medium">
              Regístrate gratis
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-red-700"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-white">
            <h2 className="text-3xl font-black tracking-tight mb-4">
              Métricas reales para decisiones reales
            </h2>
            <p className="text-red-100 text-lg">
              Descubre cuánto están sobre-reportando tus plataformas de publicidad 
              y toma decisiones basadas en datos reales.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
